import React, { useEffect, useState, useContext, useRef } from "react";
import { auth, db } from "../firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  addDoc, 
  where,
  getDocs,
  onSnapshot,
  orderBy,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from "firebase/firestore";
  import { EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
  import { arrayUnion } from "firebase/firestore";
  import { AuthContext } from "../context/AuthContext";


const reauthenticateUser = async (password) => {
  const user = auth.currentUser;
  const credential = EmailAuthProvider.credential(user.email, password);

  try {
    await reauthenticateWithCredential(user, credential);
    return true;
  } catch (error) {
    console.error("❌ Re-authentication failed:", error);
    return false;
  }
};


function MyAccountPage() {
  const { user } = useContext(AuthContext);
  const [userInfo, setUserInfo] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [showAllPosts, setShowAllPosts] = useState(false);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [openReplyBoxId, setOpenReplyBoxId] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactMessage, setContactMessage] = useState("");

  const [city, setCity] = useState("");
  const [workLocation, setWorkLocation] = useState("");
  const [company, setCompany] = useState("");
  const [department, setDepartment] = useState("");
  const [position, setPosition] = useState("");
  const [yearsOnJob, setYearsOnJob] = useState("");
  const [employmentStatus, setEmploymentStatus] = useState("");
  const [readMessageIds, setReadMessageIds] = useState(new Set());
  const messageRefs = useRef({});
  const [hasUnreadReplies, setHasUnreadReplies] = useState(false);


  const unreadCount = messages.filter(
  (msg) => msg.direction === "received" && !msg.read && !readMessageIds.has(msg.id)
).length;


  // ✅ Function to send message to Admin
const handleSendContactMessage = async () => {
  if (!contactMessage.trim()) return alert("Please enter a message.");

  const user = auth.currentUser;
  if (!user) return;

  try {
    // ✅Find Admin UID by username
    const adminQuery = query(collection(db, "users"), where("username", "==", "Admin"));
    const adminSnap = await getDocs(adminQuery);

    if (adminSnap.empty) {
      alert("Admin account not found.");
      return;
    }

    const adminUID = adminSnap.docs[0].id;

    await addDoc(collection(db, "messages"), {
      from: user.uid,
      to: adminUID,
      text: contactMessage,
      timestamp: new Date(),
      read: false,
      replies: [],
    });

    setContactMessage("");
    setShowContactModal(false);
    alert("Message sent to Admin.");
  } catch (err) {
    console.error("❌ Error sending contact message:", err);
    alert("Failed to send message. Please try again.");
  }
};


  useEffect(() => {
    const fetchUserInfo = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const data = userSnap.data();
        setUserInfo(data);
        setCity(data.city || "");
        setWorkLocation(data.workLocation || "");
        setCompany(data.company || "");
        setDepartment(data.department || "");
        setPosition(data.position || "");
        setYearsOnJob(data.yearsOnJob || "");
        setEmploymentStatus(data.employmentStatus || "");
      }
    };

    fetchUserInfo();
  }, []);

  useEffect(() => {
    const fetchUserPosts = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const postQuery = query(
        collection(db, "reviews"),
        where("userId", "==", user.uid)
      );
      const postSnap = await getDocs(postQuery);
      const posts = postSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setUserPosts(posts.sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.()));
    };

    fetchUserPosts();
  }, []);

  useEffect(() => {
  const user = auth.currentUser;
  if (!user) return;

  const toQuery = query(
    collection(db, "messages"),
    where("to", "==", user.uid),
    orderBy("timestamp", "desc")
  );

  const fromQuery = query(
    collection(db, "messages"),
    where("from", "==", user.uid),
    orderBy("timestamp", "desc")
  );

  const unsubTo = onSnapshot(toQuery, async (snapshot) => {
  const newMessages = [];

  const toMessages = await Promise.all(
    snapshot.docs.map(async (docSnap) => {
      const data = docSnap.data();

      let senderUsername = data.senderUsername || "Unknown";
      if (!data.senderUsername && data.from) {
        const userRef = doc(db, "users", data.from);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          senderUsername = userSnap.data().username || "Unknown";
        }
      }

      const message = {
        id: docSnap.id,
        ...data,
        direction: "received",
        senderUsername,
      };

	// ⬇️ Skip messages deleted for current user
if (message.deletedFor?.includes(auth.currentUser.uid)) {
  return null;
}

      // ✅ Check if this is a new message not yet in state
      const exists = messages.some((m) => m.id === docSnap.id);
      if (!exists) newMessages.push(message);

      return message;
    })
  );


  setMessages((prev) => {
    const sent = prev.filter((m) => m.direction === "sent");
    const all = [...sent, ...toMessages].filter((m) => m !== null); 
    return all.sort((a, b) => b.timestamp?.toDate?.() - a.timestamp?.toDate?.());
  });
});


  const unsubFrom = onSnapshot(fromQuery, async (fromSnap) => {
  const fromMessages = await Promise.all(
    fromSnap.docs.map(async (docSnap) => {
      const data = docSnap.data();
      let recipientUsername = "Unknown";

      if (data.to) {
        const userRef = doc(db, "users", data.to);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          recipientUsername = userSnap.data().username || "Unknown";
        }
      }

      const message = {
        id: docSnap.id,
        ...data,
        direction: "sent",
        recipientUsername,
      };

      // ⬇️ Skip messages deleted for current user
      if (message.deletedFor?.includes(auth.currentUser.uid)) {
        return null;
      }

      return message;
    })
  );

  // Remove any nulls from filtering
  const validFromMessages = fromMessages.filter((m) => m !== null);

  setMessages((prev) => {
    const toMessages = prev.filter((m) => m.direction === "received");
    const all = [...toMessages, ...validFromMessages].filter((m) => m !== null); 
    return all.sort((a, b) => b.timestamp?.toDate?.() - a.timestamp?.toDate?.());
  });
});


  return () => {
    unsubTo();
    unsubFrom();
  };
}, []);


	useEffect(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const messageId = entry.target.getAttribute("data-id");
	
	 // ✅ Define msg inside the loop
        const msg = messages.find((m) => m.id === messageId);

        if (
          entry.isIntersecting &&
          msg &&
          msg.to === auth.currentUser.uid &&
          !msg.read &&
          !readMessageIds.has(messageId)
        ) {
          // Delay marking as read
          setTimeout(async () => {
            setReadMessageIds((prev) => new Set(prev).add(messageId));

            // Mark in Firestore
            try {
              await updateDoc(doc(db, "messages", messageId), {
                read: true,
              });

	   // 🔄 Also mark replies as read if present
if (msg.replies && msg.replies.length > 0 && (!msg.readRepliesFor || !msg.readRepliesFor.includes(auth.currentUser.uid))) {
  try {
    await updateDoc(doc(db, "messages", messageId), {
      readRepliesFor: arrayUnion(auth.currentUser.uid),
    });
  } catch (err) {
    console.error("❌ Error marking replies as read:", err);
  }
}


            } catch (err) {
              console.error("Error marking message as read:", err);
            }
          }, 3000); // 3-second delay
        }
      });
    },
    {
      threshold: 0.5, // Message must be 50% visible
    }
  );

  // Observe current message elements
  Object.values(messageRefs.current).forEach((el) => {
    if (el) observer.observe(el);
  });

  return () => observer.disconnect(); // Cleanup
}, [messages, readMessageIds]);


  const handleReply = async (messageId) => {
  const reply = replyText[messageId]?.trim();
  const user = auth.currentUser;
  if (!reply || !user) return;

  try {
    console.log("🔄 Starting reply process...");

    const messageRef = doc(db, "messages", messageId);
    const messageSnap = await getDoc(messageRef);

    if (!messageSnap.exists()) {
      console.error("❌ Message not found.");
      return;
    }

    const existingReplies = messageSnap.data().replies || [];
    console.log("📨 Existing replies:", existingReplies.length);

    const newReply = {
      text: reply,
      from: user.uid,
      timestamp: new Date()
    };

    const updatedReplies = [...existingReplies, newReply];
    console.log("📝 Sending new replies array:", updatedReplies);

    await updateDoc(messageRef, {
      replies: updatedReplies
    });

    console.log("✅ Reply sent.");
    setReplyText((prev) => ({ ...prev, [messageId]: "" }));
    setOpenReplyBoxId(null);
  } catch (err) {
    console.error("❌ Error sending threaded reply:", err);
  }
};


  const togglePostLimit = () => setShowAllPosts((prev) => !prev);

  const handleLogout = () => {
    auth.signOut();
    window.location.href = "/";
  };

  const handleSaveProfile = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      await updateDoc(doc(db, "users", user.uid), {
        city,
        workLocation,
        company,
        department,
        position,
        yearsOnJob,
        employmentStatus,
      });
      setUserInfo((prev) => ({
        ...prev,
        city,
        workLocation,
        company,
        department,
        position,
        yearsOnJob,
        employmentStatus,
      }));
      setShowEditModal(false);
    } catch (err) {
      console.error("Error saving profile:", err);
    }
  };

  
  const handleDeleteMessage = async (messageId) => {
  const confirmDelete = window.confirm("Are you sure you want to delete this message?");
  if (!confirmDelete) return;

  try {
    const messageRef = doc(db, "messages", messageId);
    await updateDoc(messageRef, {
      deletedFor: arrayUnion(auth.currentUser.uid)
    });
  } catch (err) {
    console.error("❌ Error marking message as deleted:", err);
  }
};


	const handleDeletePost = async (postId) => {
  const confirmDelete = window.confirm("Are you sure you want to delete this post?");
  if (!confirmDelete) return;

  try {
    await deleteDoc(doc(db, "reviews", postId));  // Delete from Firestore
    setUserPosts((prev) => prev.filter((post) => post.id !== postId));  // Remove from local state
  } catch (err) {
    console.error("❌ Error deleting post:", err);
  }
};


	const handleDeleteAccount = async () => {
  const confirmDelete = window.confirm("Are you sure you want to delete your account? This action cannot be undone.");
  if (!confirmDelete) return;

  const user = auth.currentUser;
  if (!user) return;

  try {
    // ✅ 1. Delete user's messages (sent and received)
    const sentMessagesQuery = query(collection(db, "messages"), where("from", "==", user.uid));
    const receivedMessagesQuery = query(collection(db, "messages"), where("to", "==", user.uid));

    const sentSnap = await getDocs(sentMessagesQuery);
    const receivedSnap = await getDocs(receivedMessagesQuery);

    const sentDeletes = sentSnap.docs.map((docSnap) => deleteDoc(docSnap.ref));
    const receivedDeletes = receivedSnap.docs.map((docSnap) => deleteDoc(docSnap.ref));
    await Promise.all([...sentDeletes, ...receivedDeletes]);

    // ✅ 2. Delete user profile
    await deleteDoc(doc(db, "users", user.uid));

    // ✅ 3. Delete Firebase Auth account
    await user.delete();

    // ✅ 4. Force logout after delete
    await auth.signOut();  

    // ✅ 5. Redirect
    window.location.href = "/";
  } catch (err) {
    console.error("❌ Error deleting account:", err);
    alert("Failed to delete account. Please re-authenticate and try again.");
  }
};

const handleContactSupport = async () => {
  if (!user || !user.displayName) {
    alert("You must be logged in to contact support.");
    return;
  }

  const adminUsername = "Admin"; // must match the Admin username in Firestore
  const supportMessage = "Hi Admin, I need help with something.";

  try {
    const messagesRef = collection(db, "messages");
    await addDoc(messagesRef, {
      from: user.displayName,
      to: adminUsername,
      text: supportMessage,
      timestamp: serverTimestamp(),
      read: false,
      thread: [], // optional: use if you support replies
    });
    alert("Message sent to Admin.");
  } catch (error) {
    console.error("Failed to send message:", error);
    alert("Failed to send message to Admin.");
  }
};



  return (
    <div style={{ display: "flex", gap: "40px", padding: "20px" }}>
      {/* Left Column: User Info + Buttons + Posts */}
      <div style={{ flex: 1 }}>
        {userInfo && (
          <div style={{ marginBottom: "20px" }}>
            <h2>{userInfo.username}</h2>
            <p>Email: {userInfo.email}</p>
            <p>State: {userInfo.state}</p>
            <p>Company: {userInfo.company}</p>
            <p>Department: {userInfo.department}</p>
            <p>Position: {userInfo.position}</p>
            <p>Years on Job: {userInfo.yearsOnJob}</p>
            <p>Employment Status: {userInfo.employmentStatus}</p>
            <button onClick={() => setShowEditModal(true)} className="modern-button">
              Edit Profile
            </button>
            <button onClick={handleLogout} className="modern-button" style={{ marginLeft: "10px", marginRight: "5px" }}>
              Logout
            </button>


<button onClick={() => setShowContactModal(true)} className="modern-button" style={{ marginLeft: "5px", marginRight: "10px" }}>
  Contact Admin
</button>

	   <button onClick={handleDeleteAccount} className="modern-button">
  Delete Account
</button>

          </div>
        )}

        {showEditModal && (
          <div style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "#fff",
            padding: "20px",
            boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
            borderRadius: "8px",
            width: "350px",
            zIndex: 1000
          }}>
            <h3 style={{ textAlign: "center", marginBottom: "10px" }}>Edit Profile</h3>


            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <label>City</label>
              <input type="text" className="modern-input" value={city} onChange={(e) => setCity(e.target.value)} />
              <label>Work Location</label>
              <input type="text" className="modern-input" value={workLocation} onChange={(e) => setWorkLocation(e.target.value)} />
              <label>Company</label>
              <input type="text" className="modern-input" value={company} onChange={(e) => setCompany(e.target.value)} />
              <label>Department</label>
              <input type="text" className="modern-input" value={department} onChange={(e) => setDepartment(e.target.value)} />
              <label>Position</label>
              <input type="text" className="modern-input" value={position} onChange={(e) => setPosition(e.target.value)} />
              <label>Years on Job</label>
              <select className="modern-input" value={yearsOnJob} onChange={(e) => setYearsOnJob(e.target.value)}>
                <option value="">Select</option>
                <option value="Less than 1y">Less than 1 year</option>
                <option value="1y-5y">1-5 years</option>
                <option value="Over 5y">More than 5 years</option>
              </select>
              <label>Employment Status</label>
              <select className="modern-input" value={employmentStatus} onChange={(e) => setEmploymentStatus(e.target.value)}>
                <option value="">Select</option>
                <option value="Current Employee">Current Employee</option>
                <option value="Former Employee">Former Employee</option>
              </select>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "15px" }}>
              <button className="modern-button" onClick={handleSaveProfile}>Save</button>
              <button className="modern-button" onClick={() => setShowEditModal(false)}>Cancel</button>
            </div>
          </div>
        )}

        <h3>Your Posts</h3>
        {(showAllPosts ? userPosts : userPosts.slice(0, 20)).map((post) => (
          <div key={post.id} style={{ position: "relative", borderBottom: "1px solid #ccc", marginBottom: "0px", padding: "0px" }}>

            <h4>
              {post.company} ({post.city}, {post.state})
            </h4>
            <p>{post.comments}</p>
            <p>
              <small>
                {post.createdAt?.toDate?.().toLocaleDateString() || "Unknown date"}
              </small>
            </p>

	 {/* 🔴 Delete Button for Posts */}
  <button
  onClick={() => handleDeletePost(post.id)}
  style={{
    position: "absolute",
    bottom: "10px",
    right: "10px",
    background: "transparent",
    border: "none",
    color: "gray",  // Neutral gray
    fontWeight: "bold",
    fontSize: "16px",  // Optional: make the X a bit larger
    cursor: "pointer",
  }}
>
  X
</button>

{showContactModal && (
  <div style={{
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    background: "#fff",
    padding: "20px",
    boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
    borderRadius: "8px",
    width: "350px",
    zIndex: 1000
  }}>
    <h3 style={{ textAlign: "center", marginBottom: "10px" }}>Message to Admin</h3>
    <textarea
      value={contactMessage}
      onChange={(e) => setContactMessage(e.target.value)}
      placeholder="Your message..."
      style={{ width: "100%", minHeight: "100px", marginBottom: "10px" }}
    ></textarea>
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <button onClick={handleSendContactMessage} className="modern-button">Send</button>
      <button onClick={() => setShowContactModal(false)} className="modern-button">Cancel</button>
    </div>
  </div>
)}

</div>

          
        ))}
        {userPosts.length > 20 && (
          <button onClick={togglePostLimit} style={{ marginTop: "10px" }}>
            {showAllPosts ? "Show Less Posts" : "Show More Posts"}
          </button>
        )}
      </div>

      {/* ✅ Right Column: Messages */}
      <div style={{ flex: 1 }}>
        <h3>
  Direct Messages {unreadCount > 0 && <span style={{ color: "gold" }}>• {unreadCount} unread</span>}
</h3>

       {messages.length === 0 ? (
  <p>No messages yet.</p>
) : (
  messages.map((msg) => (
    <div
  key={msg.id}
  data-id={msg.id}
  ref={(el) => (messageRefs.current[msg.id] = el)}
  style={{
    backgroundColor: msg.direction === "sent" ? "#f0f0f0" : "#e6f7ff",
    padding: "10px",
    borderRadius: "6px",
    marginBottom: "15px",
    position: "relative",
  }}
>
  {/* 🔶 Yellow dot indicator */}
  {msg.direction === "received" && !msg.read && !readMessageIds.has(msg.id) && (
    <div
      style={{
        position: "absolute",
        top: "10px",
        right: "10px",
        width: "10px",
        height: "10px",
        backgroundColor: "gold",
        borderRadius: "50%",
         }}
    ></div>
     )}

	{/* Delete Button for DMs */}
<button
  onClick={() => handleDeleteMessage(msg.id)}
  style={{
    position: "absolute",
    bottom: "10px",
    right: "10px", // ⬅️ Use right instead of left
    background: "transparent",
    border: "none",
    color: "gray",  // Neutral gray
    fontWeight: "bold",
    cursor: "pointer",
  }}
>
  X
</button>

      <p>
        <strong>
          {msg.direction === "sent" ? "To" : "From"}:
        </strong>{" "}
        {msg.direction === "sent"
          ? msg.recipientUsername || msg.to
          : msg.senderUsername || msg.from}
      </p>

      <p>{msg.text || msg.message}</p>

	 <p>
        <small>
          {msg.timestamp?.toDate?.().toLocaleString() || "Unknown time"}
        </small>
      </p>	

	{msg.replies && msg.replies.length > 0 && (
  <div style={{ marginTop: "10px", paddingLeft: "10px", borderLeft: "2px solid #ccc" }}>
   
    {msg.replies.map((reply, i) => (
      <div key={i} style={{ marginTop: "5px" }}>
        <p style={{ marginBottom: "2px" }}><strong>
  {reply.from === auth.currentUser.uid
    ? "You"
    : (auth.currentUser.uid === msg.to ? msg.senderUsername : msg.recipientUsername) || "User"}
:</strong>


{reply.text}</p>
        <small>{new Date(reply.timestamp?.toDate?.() || reply.timestamp).toLocaleString()}</small>
      </div>
    ))}
  </div>
)}


     
      {/* ✅ Display reply if it exists */}
      {msg.reply && (
        <div
          style={{
            marginTop: "10px",
            paddingLeft: "10px",
            borderLeft: "2px solid #ccc",
            background: "#fff",
            padding: "8px",
            borderRadius: "5px",
          }}
        >
          <p><strong>Reply:</strong> {msg.reply}</p>
          <p>
            <small>
              {msg.repliedAt?.toDate?.().toLocaleString() || "Unknown time"}
            </small>
          </p>
        </div>
      )}

      {/* ✅ Reply button + box (only for received messages) */}
   {/* ✅ Reply button + box (only for received messages) */}
{(msg.direction !== "sent" || (msg.direction === "sent" && msg.replies?.length > 0)) && (
  <>
    {msg.deletedFor?.includes(msg.direction === "sent" ? msg.to : msg.from) ? (
      <p style={{ color: "red", marginTop: "5px" }}>
        This conversation was closed by the other user.
      </p>
    ) : (
      <>
        <button
          onClick={() =>
            setOpenReplyBoxId(openReplyBoxId === msg.id ? null : msg.id)
          }
          style={{ marginTop: "5px" }}
        >
          {openReplyBoxId === msg.id ? "Cancel" : "Reply"}
        </button>

        {openReplyBoxId === msg.id && (
          <div>
            <textarea
              placeholder="Reply..."
              value={replyText[msg.id] || ""}
              onChange={(e) =>
                setReplyText((prev) => ({
                  ...prev,
                  [msg.id]: e.target.value,
                }))
              }
              style={{ width: "100%", marginTop: "5px" }}
            ></textarea>
            <button
              onClick={() => handleReply(msg.id)}
              style={{ marginTop: "5px" }}
            >
              Send Reply
            </button>
          </div>
        )}
      </>
    )}
  </>
)}





    </div>
  ))
)}



      </div>
    </div>
  );
}

export default MyAccountPage;
